import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Typography,
} from '@mui/material';
import {
  RUTERO_HOLD_PASSENGER_MAX,
  type RuteroOfferOptionsSelection,
} from '@bandido/shared/types';
import { CONFIG } from '@frontend/config';
import { useSearchTours } from '../queries';
import {
  cleanParams,
  isIncompleteSearch,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  tourCatalogParams,
  tourCatalogStateFromParams,
} from '../urlParamChanges';
import {
  EMPTY_TOUR_PRODUCT_FILTERS,
  TourOffer,
  TourProduct,
  TourProductFilters,
  TourProductSort,
  canonicalTourLanguageFilters,
  filterTourProducts,
  groupTourProducts,
  sortTourProducts,
  tourProductCountsByProvider,
  tourProductCurrencies,
  tourProductFacetCount,
  tourLanguageOptions,
  toggleTourLanguageFilter,
  tourPlaceFromParams,
} from '../tours';
import { PlaceRef } from '../places';
import { describeTours } from '../describeSearch';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { TourProductCard } from './TourProductCard';
import { TourProductDialog } from './TourProductDialog';
import { BookingModal } from './BookingModal';
import {
  committedParty,
} from '../committedBookingContext';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import {
  useSearchResultAuthority,
  useSearchSelectionBlockedReason,
} from '../useSearchResultAuthority';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import {
  latestSearchDate,
  isSearchDate,
  reconcileSearchEndDate,
  todaySearchDate,
} from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';
import {
  OPTIONAL_COUNT_ONLY_PASSENGER_RULES,
  countOnlyPassengers,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { useBookingCaseContext } from './BookingCaseContext';
import {
  FilterChip,
  FilterGroup,
  ResultFiltersBar,
} from './ResultFiltersBar';
import {
  tourPartySizeFromParam,
  tourSearchPayloadFromParams,
} from '../tourSearchPayload';

/**
 * Buscador de experiencias. El destino se elige contra el maestro de lugares
 * (`PlaceAutocomplete`, `servedBy` de
 * los conectores tours, sin `kinds` — ineedtours mapea ciudades y zonas) y
 * la búsqueda manda `destination: plc_*`, que Rutero resuelve por conector.
 */
export function ToursSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'tours', dateParam: 'desde' });
  const searchPayload = useMemo(
    () => tourSearchPayloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchTours(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('tours', CONFIG.AGENCY_ID, searchPayload)
    : undefined;

  const [from, setFrom] = useState(() => url.params.desde ?? '');
  const [to, setTo] = useState(() => url.params.hasta ?? '');
  const [toDateOpen, setToDateOpen] = useState(false);
  const [destinationPlace, setDestinationPlace] = useState<PlaceRef | null>(() =>
    tourPlaceFromParams(url.params.destino, url.params.destinoNombre));
  const destinationId = destinationPlace?.id;
  const destinationName = destinationPlace?.name;
  const [pax, setPax] = useState<number | undefined>(() =>
    tourPartySizeFromParam(url.params.pax));
  const { partySuggestion, consumePartySuggestion } = useBookingCaseContext();
  useEffect(() => {
    if (!partySuggestion) return;
    setPax(partySuggestion.size);
    consumePartySuggestion(partySuggestion.revision);
  }, [partySuggestion, consumePartySuggestion]);
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<TourOffer> | null
  >(null);
  const [bookingSelection, setBookingSelection] =
    useState<RuteroOfferOptionsSelection>();
  const { filters, sort } = useMemo(
    () => tourCatalogStateFromParams(url.params),
    [url.params],
  );
  const bookingOffer = bookingQuote?.offer ?? null;
  const committedDestinationName = searchPayload
    ? url.params.destinoNombre ??
      searchPayload.destination ?? ''
    : '';

  const minFromDate = todaySearchDate();
  const minToDate = from ? latestSearchDate(minFromDate, from) : minFromDate;
  const canSearch = Boolean(
    destinationId &&
      (!from || (isSearchDate(from) && from >= minFromDate)) &&
      (!to || (Boolean(from) && isSearchDate(to) && to >= minToDate)),
  );

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    desde: from || undefined,
    hasta: to || undefined,
    // El plc_* viaja opaco; Bandido no lo traduce a códigos del proveedor.
    destino: destinationId,
    // El nombre no se busca: repinta el autocomplete al volver por URL.
    destinoNombre: destinationName,
    pax: pax ? String(pax) : undefined,
  };
  const draftPayload = tourSearchPayloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);
  const selectionBlockedReason = useSearchSelectionBlockedReason(resultAuthority);

  /** Vacía la búsqueda: campos, URL y memoria de navegación. */
  const handleClear = () => {
    setFrom('');
    setTo('');
    setDestinationPlace(null);
    setPax(undefined);
    url.reset();
  };

  const handleSearch = () => {
    if (!canSearch || !destinationId || !destinationName) return;
    // Las facetas pertenecen a la generación visible; una búsqueda nueva no
    // hereda filtros capaces de esconder sus resultados.
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('tours', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit({
      ...formParams,
      ...tourCatalogParams(EMPTY_TOUR_PRODUCT_FILTERS, 'relevance'),
      tourProveedor: undefined,
      tourProducto: undefined,
    });
  };

  const products = useMemo(
    () => groupTourProducts((search.data?.results ?? []) as TourOffer[]),
    [search.data],
  );
  const visibleProducts = useMemo(
    () => sortTourProducts(filterTourProducts(products, filters), sort),
    [products, filters, sort],
  );
  const providers = useMemo(
    () => [...new Set(products.map((product) => product.provider))].sort(),
    [products],
  );
  const categories = useMemo(
    () => [
      ...new Set(
        products
          .map((product) => product.category)
          .filter((category): category is string => Boolean(category)),
      ),
    ].sort(),
    [products],
  );
  const languages = useMemo(
    () => tourLanguageOptions(products, i18n.language),
    [products, i18n.language],
  );
  const activeLanguageIdentities = useMemo(
    () => canonicalTourLanguageFilters(products, filters.languages),
    [products, filters.languages],
  );
  const countsByProvider = useMemo(
    () =>
      tourProductCountsByProvider(
        products,
        search.data?.providers?.responded,
      ),
    [products, search.data?.providers?.responded],
  );
  const selectedProduct = useMemo(
    () =>
      products.find(
        (product) =>
          product.provider === url.params.tourProveedor &&
          (product.productRef ?? product.title) === url.params.tourProducto,
      ),
    [products, url.params.tourProducto, url.params.tourProveedor],
  );
  const mixedCurrencies = tourProductCurrencies(products).length > 1;
  const activeFilters =
    filters.providers.length + filters.categories.length + filters.languages.length;
  const facetCount = (patch: Partial<TourProductFilters>) =>
    tourProductFacetCount(products, filters, patch);
  const toggleInList = (values: string[], value: string) =>
    values.includes(value)
      ? values.filter((candidate) => candidate !== value)
      : [...values, value];

  const updateFilters = (
    next: TourProductFilters | ((current: TourProductFilters) => TourProductFilters),
  ) => {
    const resolved = typeof next === 'function' ? next(filters) : next;
    url.update(tourCatalogParams(resolved, sort));
  };

  const updateSort = (next: TourProductSort) => {
    url.update(tourCatalogParams(filters, next));
  };

  const openProduct = (product: TourProduct) => {
    url.update({
      tourProveedor: product.provider,
      tourProducto: product.productRef ?? product.title,
    });
  };

  const closeProduct = () => {
    url.update({ tourProveedor: undefined, tourProducto: undefined });
  };

  const handleBook = (
    offer: TourOffer,
    selection: RuteroOfferOptionsSelection,
  ) => {
    setBookingQuote(
      captureSelectableSearchQuote(
        offer,
        search.dataUpdatedAt,
        'tours',
        CONFIG.AGENCY_ID,
        searchPayload,
        Date.now(),
        formDiverged,
      ),
    );
    setBookingSelection(selection);
    closeProduct();
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.tours.destination')} grow width={260}>
          <PlaceAutocomplete
            label={t('travelServices.tours.destination')}
            vertical="tours"
            value={destinationPlace}
            onChange={setDestinationPlace}
            width="100%"
            required
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.tours.from')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.tours.from')}
            value={from}
            minDate={minFromDate}
            onChange={(nextDate) => {
              setFrom(nextDate);
              setTo((current) => reconcileSearchEndDate(nextDate, current));
            }}
            onAccept={() => {
              if (from) setToDateOpen(true);
            }}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.tours.to')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.tours.to')}
            value={to}
            onChange={setTo}
            minDate={minToDate}
            open={toDateOpen}
            onOpen={() => setToDateOpen(true)}
            onClose={() => setToDateOpen(false)}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.passengerPicker.label')}
          width={SEARCH_PASSENGER_FIELD_WIDTH}
        >
          <SearchPassengerPicker
            value={
              pax
                ? countOnlyPassengers(pax, RUTERO_HOLD_PASSENGER_MAX)
                : []
            }
            onChange={(nextPassengers) =>
              setPax(nextPassengers.length || undefined)}
            rules={OPTIONAL_COUNT_ONLY_PASSENGER_RULES}
            maxPassengers={RUTERO_HOLD_PASSENGER_MAX}
          />
        </SearchField>
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="tours"
          describe={(params) => describeTours(params, t, i18n.language)}
        />
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link recortado o
          editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeTours(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="product" />}
      {search.isError && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      <ProviderStatusStrip
        providers={search.data?.providers}
        countsByProvider={countsByProvider}
      />

      {search.data && !search.isLoading && (
        products.length === 0 ? (
          <Alert severity="info">{t('travelServices.noResults')}</Alert>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <ResultFiltersBar
              activeCount={activeFilters}
              onClear={() => updateFilters(EMPTY_TOUR_PRODUCT_FILTERS)}
              resultCount={visibleProducts.length}
              sort={{
                value: sort,
                onChange: (value) => updateSort(value as TourProductSort),
                options: [
                  {
                    value: 'relevance',
                    label: t('travelServices.tours.sortRelevance'),
                  },
                  {
                    value: 'title',
                    label: t('travelServices.tours.sortTitle'),
                  },
                  {
                    value: 'price',
                    label: t('travelServices.tours.sortPrice'),
                  },
                ],
              }}
            >
              {(stacked) => (
                <>
                  <FilterGroup
                    label={t('travelServices.tours.providerFilter')}
                    stacked={stacked}
                  >
                    {providers.map((provider) => (
                      <FilterChip
                        key={provider}
                        label={provider}
                        count={facetCount({ providers: [provider] })}
                        active={filters.providers.includes(provider)}
                        onClick={() =>
                          updateFilters((current) => ({
                            ...current,
                            providers: toggleInList(current.providers, provider),
                          }))
                        }
                      />
                    ))}
                  </FilterGroup>
                  {categories.length > 0 && (
                    <FilterGroup
                      label={t('travelServices.tours.categoryFilter')}
                      stacked={stacked}
                    >
                      {categories.map((category) => (
                        <FilterChip
                          key={category}
                          label={category}
                          count={facetCount({ categories: [category] })}
                          active={filters.categories.includes(category)}
                          onClick={() =>
                            updateFilters((current) => ({
                              ...current,
                              categories: toggleInList(current.categories, category),
                            }))
                          }
                        />
                      ))}
                    </FilterGroup>
                  )}
                  {languages.length > 0 && (
                    <FilterGroup
                      label={t('travelServices.tours.languageFilter')}
                      stacked={stacked}
                    >
                      {languages.map((language) => {
                        const [identity] = canonicalTourLanguageFilters(
                          products,
                          [language.value],
                        );
                        const active = activeLanguageIdentities.includes(identity);
                        return (
                          <FilterChip
                            key={identity}
                            label={language.label}
                            count={facetCount({ languages: [language.value] })}
                            active={active}
                            onClick={() =>
                              updateFilters((current) => ({
                                ...current,
                                languages: toggleTourLanguageFilter(
                                  products,
                                  current.languages,
                                  identity,
                                ),
                              }))
                            }
                          />
                        );
                      })}
                    </FilterGroup>
                  )}
                </>
              )}
            </ResultFiltersBar>

            {mixedCurrencies && sort === 'price' && (
              <Alert severity="info">
                {t('travelServices.tours.mixedCurrencies')}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('travelServices.tours.productsShown', {
                count: visibleProducts.length,
                total: products.length,
              })}
            </Typography>
            {visibleProducts.length === 0 ? (
              <Alert severity="info">
                {t('travelServices.tours.noFilteredProducts')}
              </Alert>
            ) : (
              visibleProducts.map((product, index) => (
                <TourProductCard
                  key={`${product.provider}:${product.productRef ?? product.title}`}
                  product={product}
                  index={index}
                  disabled={!resultAuthority.canSelectResult}
                  onOpen={openProduct}
                />
              ))
            )}
          </Box>
        )
      )}

      <TourProductDialog
        product={selectedProduct}
        partySize={searchPayload?.partySize}
        disabled={!resultAuthority.canSelectResult}
        disabledReason={selectionBlockedReason}
        onContinue={handleBook}
        onClose={closeProduct}
      />

      <BookingModal
        vertical="tours"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={
          bookingOffer
            ? [
              bookingOffer.title ?? '',
              [
                committedDestinationName,
                bookingOffer.startDate?.split('-').reverse().join('/'),
              ]
                .filter(Boolean)
                .join(' · '),
            ].filter(Boolean)
            : undefined
        }
        party={committedParty({ vertical: 'tours', payload: searchPayload })}
        initialOfferSelection={bookingSelection}
        onClose={() => {
          setBookingQuote(null);
          setBookingSelection(undefined);
        }}
      />
    </Box>
  );
}
